import { jsx as _jsx } from "react/jsx-runtime";
import { BadgeCheck, Bell, BookOpen, Boxes, CalendarDays, Crosshair, FileBadge, FileText, FolderKanban, FolderOpen, HelpCircle, History, KeyRound, LayoutDashboard, Library, Link2, MessageSquare, Network, Rocket, ScrollText, Settings, ShieldCheck, Sparkles, Target, Telescope, UserCog, Users, UsersRound } from 'lucide-react';
import { useMemo } from 'react';
import { SECTION_KEYWORDS, useProgramSections } from '../components/programSections';
import { REGISTRY_TABS } from '../pages/Delivery/Registries/registryTabs';
const REGISTRY_EXTRAS = {
    contracts: {
        description: 'Awarded contracts and their terms',
        keywords: [
            'awards',
            'PO'
        ]
    },
    companies: {
        description: 'Companies you track',
        keywords: [
            'vendors',
            'primes',
            'subs'
        ]
    },
    contacts: {
        description: 'People at companies and agencies',
        keywords: [
            'people',
            'POC'
        ]
    },
    govorgs: {
        description: 'Government agencies and offices',
        keywords: [
            'agency',
            'government',
            'customer'
        ]
    },
    capabilities: {
        description: 'What your company can do',
        keywords: [
            'skills',
            'past performance'
        ]
    },
    vehicles: {
        description: 'Contract vehicles',
        keywords: [
            'IDIQ',
            'GWAC',
            'BPA',
            'schedule'
        ]
    },
    lcats: {
        description: 'Labor category definitions',
        keywords: [
            'LCAT',
            'LCATs',
            'labor cat',
            'positions'
        ]
    },
    'rate-book': {
        description: 'Billing rates by labor category',
        keywords: [
            'rates',
            'pricing'
        ]
    },
    roles: {
        description: 'Program role types',
        keywords: [
            'roles',
            'staffing'
        ]
    },
    'pricing-sheets': {
        description: 'Pricing sheets',
        keywords: [
            'pricing',
            'rates',
            'CLIN'
        ]
    }
};
export function useCommandPaletteItems({ navigate, isAdmin, areasLoading, reachable, growthOnly, programId }) {
    const { capture, opportunities, delivery, library } = reachable;
    const program = useProgramSections(programId);
    return useMemo(()=>{
        const show = (flag)=>!areasLoading && flag;
        const groups = [
            [
                'Home',
                [
                    growthOnly ? {
                        id: 'home',
                        label: 'Growth Home',
                        href: '/growth/home',
                        description: 'Your authorized company portfolio and active work',
                        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {}),
                        keywords: [
                            'home',
                            'start'
                        ]
                    } : {
                        id: 'home',
                        label: 'Dashboard',
                        href: '/dashboard',
                        description: 'Your overview',
                        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {}),
                        keywords: [
                            'home',
                            'start'
                        ]
                    }
                ]
            ]
        ];
        if (show(capture || opportunities)) {
            const growth = [
                {
                    id: 'growth',
                    label: 'Growth hub',
                    href: '/growth',
                    description: 'Pursuits, opportunities and landscapes',
                    icon: /*#__PURE__*/ _jsx(Rocket, {}),
                    keywords: [
                        'business development',
                        'BD',
                        'pipeline'
                    ]
                }
            ];
            if (capture) {
                growth.push({
                    id: 'capture',
                    label: 'Pursuits',
                    href: '/capture',
                    description: 'Active pursuits and capture plans',
                    icon: /*#__PURE__*/ _jsx(Crosshair, {}),
                    keywords: [
                        'capture',
                        'bids',
                        'pipeline'
                    ]
                });
            }
            if (opportunities) {
                growth.push({
                    id: 'opportunities',
                    label: 'Opportunities',
                    href: '/opportunities',
                    description: 'Federal opportunities to evaluate',
                    icon: /*#__PURE__*/ _jsx(Target, {}),
                    keywords: [
                        'RFP',
                        'solicitation',
                        'SAM',
                        'notices',
                        'bids'
                    ]
                }, {
                    id: 'landscapes',
                    label: 'Landscapes',
                    href: '/landscapes',
                    description: 'Market landscapes across agencies and competitors',
                    icon: /*#__PURE__*/ _jsx(Telescope, {}),
                    keywords: [
                        'market',
                        'competitors',
                        'forecast'
                    ]
                });
            }
            groups.push([
                'Growth',
                growth
            ]);
        }
        if (show(delivery)) {
            groups.push([
                'Delivery',
                [
                    {
                        id: 'delivery',
                        label: 'Delivery overview',
                        href: '/delivery',
                        description: 'Programs, audits, and the rest of the delivery domain',
                        icon: /*#__PURE__*/ _jsx(Boxes, {}),
                        keywords: [
                            'Delivery'
                        ]
                    },
                    {
                        id: 'command-center',
                        label: 'Command Center',
                        href: '/delivery/command-center',
                        description: 'Cross-project rollups across portfolios and sectors',
                        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {}),
                        keywords: [
                            'COO',
                            'rollup',
                            'portfolio',
                            'funding',
                            'risk'
                        ]
                    },
                    {
                        id: 'deliverables',
                        label: 'Deliverables',
                        href: '/delivery/command-center/deliverables',
                        description: 'Deliverables across every authorized program',
                        icon: /*#__PURE__*/ _jsx(FileText, {}),
                        keywords: [
                            'CDRL',
                            'due',
                            'submissions'
                        ]
                    },
                    {
                        id: 'org',
                        label: 'Organization',
                        href: '/delivery/org',
                        description: 'Sectors, business units, portfolios — the org hierarchy',
                        icon: /*#__PURE__*/ _jsx(Network, {}),
                        keywords: [
                            'org chart',
                            'hierarchy',
                            'sectors',
                            'business units',
                            'portfolios'
                        ]
                    },
                    {
                        id: 'programs',
                        label: 'Programs',
                        href: '/delivery/programs',
                        description: 'Active program records, audits, deviations and corrective actions',
                        icon: /*#__PURE__*/ _jsx(FolderKanban, {}),
                        keywords: [
                            'audits',
                            'deviations',
                            'corrective actions'
                        ]
                    },
                    {
                        id: 'audit-templates',
                        label: 'Audit Templates',
                        href: '/delivery/audit-templates',
                        description: 'Reusable audit definitions and their question sets',
                        icon: /*#__PURE__*/ _jsx(FileBadge, {}),
                        keywords: [
                            'audit',
                            'questions',
                            'checklist'
                        ]
                    },
                    {
                        id: 'evidence-board',
                        label: 'Proof & Evidence Board',
                        href: '/delivery/evidence-board',
                        description: 'Deliverable evidence and proof-of-delivery status',
                        icon: /*#__PURE__*/ _jsx(BadgeCheck, {}),
                        keywords: [
                            'proof',
                            'evidence',
                            'proof of delivery'
                        ]
                    },
                    {
                        id: 'calendar',
                        label: 'Delivery Calendar',
                        href: '/delivery/calendar',
                        description: 'Cross-project deadlines and option-period windows',
                        icon: /*#__PURE__*/ _jsx(CalendarDays, {}),
                        keywords: [
                            'calendar',
                            'deadlines',
                            'due dates',
                            'option periods',
                            'protest',
                            'debrief'
                        ]
                    }
                ]
            ]);
        }
        groups.push([
            'Registries',
            [
                {
                    id: 'registries',
                    label: 'Registries',
                    href: '/registries',
                    description: 'Companies, contacts, government orgs, vehicles, and labor categories',
                    icon: /*#__PURE__*/ _jsx(Library, {}),
                    keywords: [
                        'directory'
                    ]
                },
                ...REGISTRY_TABS.map(({ key, label })=>({
                        id: `registries-${key}`,
                        label,
                        href: `/registries?tab=${key}`,
                        description: `Registries · ${REGISTRY_EXTRAS[key]?.description ?? label}`,
                        icon: /*#__PURE__*/ _jsx(Library, {}),
                        keywords: [
                            'registry',
                            'registries',
                            ...REGISTRY_EXTRAS[key]?.keywords ?? []
                        ]
                    }))
            ]
        ]);
        const tools = [
            {
                id: 'projects',
                label: 'Projects',
                href: '/projects',
                description: 'Your proposal and project workspaces',
                icon: /*#__PURE__*/ _jsx(FolderOpen, {}),
                keywords: [
                    'proposals'
                ]
            },
            {
                id: 'chat',
                label: 'Chat',
                href: '/assistant',
                description: 'Ask the assistant',
                icon: /*#__PURE__*/ _jsx(MessageSquare, {}),
                keywords: [
                    'assistant',
                    'AI',
                    'Assist'
                ]
            },
            {
                id: 'documents',
                label: 'Documents',
                href: '/documents',
                description: 'Uploaded documents',
                icon: /*#__PURE__*/ _jsx(FileText, {}),
                keywords: [
                    'files',
                    'uploads'
                ]
            },
            {
                id: 'sharepoint',
                label: 'SharePoint',
                href: '/sharepoint-documents',
                description: 'Documents linked from SharePoint',
                icon: /*#__PURE__*/ _jsx(Link2, {}),
                keywords: [
                    'files',
                    'microsoft'
                ]
            }
        ];
        if (show(library)) {
            tools.push({
                id: 'library',
                label: 'Library',
                href: '/library',
                description: 'Reusable content and boilerplate',
                icon: /*#__PURE__*/ _jsx(BookOpen, {}),
                keywords: [
                    'boilerplate',
                    'content',
                    'answers'
                ]
            });
        }
        tools.push({
            id: 'delegate',
            label: 'Delegate my access',
            href: '/admin/access',
            description: 'Share your access with someone else',
            icon: /*#__PURE__*/ _jsx(KeyRound, {}),
            keywords: [
                'delegation',
                'share',
                'grant',
                'permissions'
            ]
        }, {
            id: 'settings',
            label: 'Settings',
            href: '/settings',
            description: 'Your account and preferences',
            icon: /*#__PURE__*/ _jsx(Settings, {}),
            keywords: [
                'preferences',
                'account',
                'profile'
            ]
        }, {
            id: 'help',
            label: 'Help',
            href: '/help',
            description: 'Guides and support',
            icon: /*#__PURE__*/ _jsx(HelpCircle, {}),
            keywords: [
                'support',
                'docs',
                'guide'
            ]
        }, {
            id: 'history',
            label: 'History',
            href: '/history',
            description: 'Your recent activity',
            icon: /*#__PURE__*/ _jsx(History, {}),
            keywords: [
                'recent'
            ]
        });
        groups.push([
            'Tools',
            tools
        ]);
        if (isAdmin) {
            const admin = (slug, label, description, node, keywords = [])=>({
                    id: `admin-${slug}`,
                    label,
                    href: `/admin/${slug}`,
                    description,
                    icon: node,
                    keywords: [
                        'admin',
                        ...keywords
                    ]
                });
            groups.push([
                'Admin',
                [
                    admin('people', 'People', 'Users in your workspace', /*#__PURE__*/ _jsx(Users, {}), [
                        'users',
                        'members'
                    ]),
                    admin('groups', 'Groups', 'Teams and groups', /*#__PURE__*/ _jsx(UsersRound, {}), [
                        'teams'
                    ]),
                    admin('permissions', 'Permissions', 'Who can do what', /*#__PURE__*/ _jsx(ShieldCheck, {}), [
                        'roles'
                    ]),
                    admin('access-rules', 'Access Rules', 'Rules that grant access', /*#__PURE__*/ _jsx(UserCog, {}), [
                        'policy'
                    ]),
                    admin('activity', 'Activity', 'Audit trail of changes', /*#__PURE__*/ _jsx(ScrollText, {}), [
                        'audit log'
                    ]),
                    admin('notifications', 'Notifications', 'Notification settings', /*#__PURE__*/ _jsx(Bell, {}), [
                        'alerts'
                    ]),
                    admin('usage', 'Usage', 'Usage and consumption', /*#__PURE__*/ _jsx(Sparkles, {}), [
                        'tokens',
                        'billing'
                    ])
                ]
            ]);
        }
        if (programId) {
            // The rail's own sections under the same visibility rules, listed first
            // so they outrank the app-wide destinations while inside a program.
            const base = `/delivery/programs/${programId}`;
            groups.unshift([
                program.programName,
                [
                    ...program.primary,
                    ...program.groups.flatMap((g)=>g.items)
                ].map((def)=>({
                        id: `program-${def.id}`,
                        label: def.label,
                        href: def.path === '' ? base : `${base}/${def.path}`,
                        description: `${program.programName} · ${def.label}`,
                        icon: def.icon,
                        keywords: [
                            program.programName,
                            ...SECTION_KEYWORDS[def.id] ?? []
                        ]
                    }))
            ]);
        }
        return groups.flatMap(([group, list])=>list.map((d)=>({
                    id: `nav-${d.id}`,
                    label: d.label,
                    description: d.description,
                    group,
                    icon: d.icon,
                    // Navigation destinations carry the one reference color (info/blue).
                    tone: 'info',
                    keywords: d.keywords,
                    onSelect: ()=>navigate(d.href)
                })));
    }, [
        navigate,
        isAdmin,
        areasLoading,
        capture,
        opportunities,
        delivery,
        library,
        growthOnly,
        programId,
        program
    ]);
}
